import { useId, useState } from 'react'

/**
 * The arcade's chart primitives.
 *
 * WHY A MODULE AND NOT INLINE SVG PER VIEW. There are now four views drawing
 * marks, and a second copy of "how a bar is shaped" is how two panels start
 * disagreeing about what a value looks like — the same reasoning that pulled
 * the avatar and the sparkline into `atoms.tsx`.
 *
 * THE RULES EVERY COMPONENT HERE HOLDS, and they are not stylistic:
 *
 *  1. NO DUAL AXIS, ANYWHERE. Two measures of different magnitude get two
 *     charts, never two y-scales. The tools view draws server VOLUME and
 *     server REACH side by side for exactly this reason: a dual-axis version
 *     invents a correlation between "loud" and "useful" that the data does not
 *     contain, which is the single reading that panel exists to prevent.
 *  2. SERIES COLOURS ARE ASSIGNED IN FIXED SLOT ORDER AND NEVER CYCLED past
 *     the palette. A seventh generated hue is indistinguishable from an
 *     existing one under colour-vision deficiency, so the callers pool the
 *     tail into one "N more" series instead. Colour follows the ENTITY, never
 *     its rank, so a filter that drops a series does not repaint the rest.
 *  3. STATUS COLOURS ARE RESERVED and always carry a label. `STATUS_COLORS`
 *     is never used for "series 4", and a denial is never red-and-nothing-else.
 *  4. TEXT WEARS TEXT TOKENS. Values, labels and legends are `--a-ink*`; the
 *     coloured mark beside them carries identity. A light categorical hue is
 *     illegible as text on either surface.
 *  5. EVERY MARK IS A HIT TARGET. Bars, dots and segments carry a `<title>`,
 *     which is a real hover tooltip AND is read by assistive technology; the
 *     line charts add a crosshair that snaps to the nearest x. Nothing is
 *     gated behind hover: every chart on the board sits beside a table or
 *     direct labels carrying the same numbers.
 *  6. THIN MARKS. Bars cap at 24px so the band's leftover is air, lines are
 *     2px, dots are at least 8px across with a 2px surface ring, and touching
 *     fills are separated by a 2px gap in the surface colour rather than by a
 *     stroke.
 *
 * Colours arrive as `theme.chartColors` — the VALIDATED series palette, not
 * the theme's neon accents. See the note on `ArcadeTheme.chartColors`.
 */

/**
 * THE NATURAL WIDTH EVERY CHART IS DRAWN AT.
 *
 * An SVG with a viewBox scales its own text along with the container, so two
 * charts drawn at different viewBox widths render their labels at different
 * SIZES even though both say `font-size: 13px`. Measured on the first render:
 * a 560-wide chart in a half column and a 720-wide chart in a full-width card
 * came out at roughly 11px and 22px on the same page.
 *
 * So every chart is drawn at ONE natural width and the CSS caps the rendered
 * width to match, which means the scale factor is at most 1 and the type is
 * consistent across the board. A chart in a narrow column scales DOWN, which
 * is fine; nothing scales up.
 */
const NATURAL_W = 560

/** Bars never fill their band: the leftover is the spacer. */
const MAX_BAR = 24
/** The 2px surface gap and surface ring that do the separating. */
const GAP = 2
/** Rounded data-end, square at the baseline. */
const END_RADIUS = 4

/** A value formatter. Every chart takes one rather than formatting inline, so
 *  a panel cannot disagree with its own table about what a number looks like. */
export type Fmt = (n: number) => string

export const fmtInt: Fmt = (n) => Math.round(n).toLocaleString()
export const fmtPct: Fmt = (n) => `${(n * 100).toFixed(1)}%`
export const fmtMs: Fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`)

/**
 * Slot colour for series `i`, in FIXED order.
 *
 * Past the palette it returns the neutral, NOT the last hue. Wrapping to the
 * last hue was the first render's bug: nine ranked topics produced three
 * identical red bars, so one colour meant three different entities. A neutral
 * says "this is beyond the palette", which is true, and the caller is expected
 * to have pooled the tail before it gets here.
 */
export const NEUTRAL_SERIES = 'var(--a-ink-faint)'

export function slotColor(colors: string[], i: number): string {
  return colors[i] ?? NEUTRAL_SERIES
}

/* ==========================================================================
 * LEGEND
 * A legend is ALWAYS present for two or more series -- the dependable
 * identity channel. One series needs none: the title already names it.
 * ======================================================================== */

export function ChartLegend({
  items,
  shape = 'rect',
}: {
  items: Array<{ label: string; color: string; hatched?: boolean }>
  /** Legends mirror the mark: a rect for bars and areas, a line for lines. */
  shape?: 'rect' | 'line'
}) {
  if (items.length < 2) return null
  return (
    <div className="chlegend">
      {items.map((it) => (
        <div key={it.label}>
          <i
            data-shape={shape}
            style={{
              background: it.hatched
                ? `repeating-linear-gradient(45deg, ${it.color} 0 3px, transparent 3px 6px)`
                : it.color,
              border: it.hatched ? `1px solid ${it.color}` : 'none',
            }}
          />
          <span>{it.label}</span>
        </div>
      ))}
    </div>
  )
}

/* ==========================================================================
 * RANKED HORIZONTAL BARS
 * The workhorse. Magnitude by category, one axis, direct value labels.
 * ======================================================================== */

export interface BarRow {
  label: string
  value: number
  /** Printed after the value: "6 people", "n=1". Never a second encoding on
   *  its own -- it supplements the bar, it does not replace it. */
  note?: string
  /**
   * Draws the bar hatched instead of solid.
   *
   * SECONDARY ENCODING, NEVER SUPPRESSION. A group under the contest floor
   * keeps its real bar and its real number; the hatch and the printed `n` say
   * "this is one or two people's week" without the view pretending the group
   * does not exist. Replacing the bar with a dash withheld a comparison and
   * concealed nothing, since the same people are named three rows up.
   */
  hatched?: boolean
}

/**
 * Ranked horizontal bars.
 *
 * MONOCHROME BY DEFAULT, and that is a considered choice rather than a
 * shortcut. A ranked bar chart carries its category name in the gutter beside
 * every bar, so hue encodes nothing that the label is not already saying — and
 * because the rows are sorted by value, a slot palette would colour bars BY
 * RANK, which means a filter that drops one category repaints all the
 * survivors. Colour follows the entity or it does not appear.
 *
 * Pass `monochrome={false}` only where the bars are legend-linked to another
 * mark of the same entities, and only when the row count is within the
 * palette.
 */
export function RankedBars({
  rows,
  colors,
  format = fmtInt,
  height,
  monochrome = true,
  label = 'Ranked bars',
}: {
  rows: BarRow[]
  colors: string[]
  format?: Fmt
  height?: number
  monochrome?: boolean
  label?: string
}) {
  if (rows.length === 0) return <p className="hint">NO DATA IN THIS WINDOW.</p>
  const max = Math.max(...rows.map((r) => r.value), 1)
  const band = 34
  const h = height ?? rows.length * band + 12
  const w = NATURAL_W
  // The left gutter carries the category names as ordinary text, which is what
  // makes this chart legible without colour at all.
  const gutter = 148
  const plot = w - gutter - 96
  const bar = Math.min(MAX_BAR, band - 12)
  const uid = useId()
  // A category name longer than the gutter comfortably holds (roughly 20
  // characters at `.chcat`'s 13px) is ellipsised on the visible label, not
  // hidden -- the row's own `<title>` below already carries the full text
  // for a hover, so nothing is lost, only the collision with the bar it
  // painted over the first time a model's display name ran long.
  const MAX_LABEL_CHARS = 20
  const shortLabel = (label: string) => (label.length > MAX_LABEL_CHARS ? `${label.slice(0, MAX_LABEL_CHARS - 1)}…` : label)

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="plot" role="img" aria-label={label} preserveAspectRatio="xMinYMin meet">
      <defs>
        {/* ONE PATTERN PER COLOUR, not one pattern using `currentColor`.
            A pattern referenced through `fill="url(#id)"` resolves
            `currentColor` against its OWN context in `<defs>`, not against the
            element that references it, so the first render drew every hatched
            bar in black instead of in its series colour. */}
        {[...new Set(rows.map((_, i) => (monochrome ? slotColor(colors, 0) : slotColor(colors, i))))].map(
          (c, ci) => (
            <pattern
              key={c}
              id={`hatch-${uid}-${ci}`}
              width="6"
              height="6"
              patternTransform="rotate(45)"
              patternUnits="userSpaceOnUse"
            >
              <rect width="6" height="6" fill="var(--a-panel)" />
              <rect width="3" height="6" fill={c} />
            </pattern>
          ),
        )}
      </defs>
      {rows.map((r, i) => {
        const color = monochrome ? slotColor(colors, 0) : slotColor(colors, i)
        const hatchIndex = [...new Set(rows.map((_, j) => (monochrome ? slotColor(colors, 0) : slotColor(colors, j))))].indexOf(color)
        const y = i * band + 6
        const len = Math.max(2, (r.value / max) * plot)
        return (
          <g key={r.label} color={color}>
            <title>{`${r.label} — ${format(r.value)}${r.note ? ` · ${r.note}` : ''}`}</title>
            {/* The whole band is the hit target, not just the painted bar. */}
            <rect x={0} y={y} width={w} height={band - GAP} fill="transparent" />
            <text x={0} y={y + bar / 2 + 4} className="chcat">
              {shortLabel(r.label)}
            </text>
            <rect
              x={gutter}
              y={y}
              width={len}
              height={bar}
              rx={END_RADIUS}
              fill={r.hatched ? `url(#hatch-${uid}-${hatchIndex})` : color}
            />
            {/* Square at the baseline: the rounded rect's left corners are
                covered so the bar grows from a flat origin. */}
            <rect x={gutter} y={y} width={Math.min(END_RADIUS, len)} height={bar} fill={r.hatched ? 'var(--a-panel)' : color} />
            <text x={gutter + len + 8} y={y + bar / 2 + 4} className="chval">
              {format(r.value)}
              {r.note ? <tspan className="chnote"> {r.note}</tspan> : null}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/* ==========================================================================
 * DUMBBELL
 * p50 to p90. The DISTANCE is the quantity of interest, so the chart draws
 * the distance directly rather than asking a reader to subtract two bars.
 * ======================================================================== */

export function Dumbbell({
  rows,
  colors,
  format = fmtMs,
  label = 'Median to 90th percentile',
}: {
  rows: Array<{ label: string; low: number; high: number }>
  colors: string[]
  format?: Fmt
  label?: string
}) {
  if (rows.length === 0) return <p className="hint">NO LATENCY DATA IN THIS WINDOW.</p>
  const max = Math.max(...rows.map((r) => r.high), 1)
  const band = 32
  const w = NATURAL_W
  const h = rows.length * band + 12
  const gutter = 148
  const plot = w - gutter - 96
  const lowColor = slotColor(colors, 0)
  const highColor = slotColor(colors, 2)

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="plot" role="img" aria-label={label} preserveAspectRatio="xMinYMin meet">
      {rows.map((r, i) => {
        const y = i * band + band / 2
        const x1 = gutter + (r.low / max) * plot
        const x2 = gutter + (r.high / max) * plot
        return (
          <g key={r.label}>
            <title>{`${r.label} — median ${format(r.low)}, 90th percentile ${format(r.high)}`}</title>
            <rect x={0} y={y - band / 2} width={w} height={band - GAP} fill="transparent" />
            <text x={0} y={y + 4} className="chcat">
              {r.label}
            </text>
            <line x1={x1} y1={y} x2={x2} y2={y} stroke="var(--a-line2)" strokeWidth={2} strokeLinecap="round" />
            {/* 2px surface ring on both dots so they stay legible where the
                two ends nearly touch. */}
            <circle cx={x1} cy={y} r={5} fill={lowColor} stroke="var(--a-panel)" strokeWidth={GAP} />
            <circle cx={x2} cy={y} r={5} fill={highColor} stroke="var(--a-panel)" strokeWidth={GAP} />
            <text x={x2 + 12} y={y + 4} className="chval">
              {format(r.high)}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/* ==========================================================================
 * DONUT
 * Legal here because the question is genuinely part-to-whole at a glance and
 * the tail is pooled so no segment is too thin to see. ALWAYS paired with a
 * ranked bar chart, because a ring cannot resolve one mid-sized segment
 * against another and a bar chart can.
 * ======================================================================== */

export function Donut({
  segments,
  colors,
  hero,
  heroLabel,
  label = 'Share of the whole',
}: {
  segments: Array<{ label: string; value: number }>
  colors: string[]
  hero: string
  heroLabel: string
  label?: string
}) {
  const total = segments.reduce((n, s) => n + s.value, 0)
  if (total <= 0) return <p className="hint">NO DATA IN THIS WINDOW.</p>
  const size = 300
  const r = 110
  const stroke = 42
  const c = size / 2
  const circumference = 2 * Math.PI * r
  // The 2px surface gap between touching segments, expressed as arc length.
  const gapArc = GAP + 1

  let offset = 0
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="plot" role="img" aria-label={label}>
      {segments.map((s, i) => {
        const frac = s.value / total
        const len = Math.max(0, frac * circumference - gapArc)
        const dash = `${len} ${circumference - len}`
        const rotation = (offset / circumference) * 360 - 90
        offset += frac * circumference
        return (
          <circle
            key={s.label}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={slotColor(colors, i)}
            strokeWidth={stroke}
            strokeDasharray={dash}
            transform={`rotate(${rotation} ${c} ${c})`}
          >
            <title>{`${s.label} — ${fmtInt(s.value)} (${fmtPct(frac)})`}</title>
          </circle>
        )
      })}
      <text x={c} y={c - 2} className="chhero" textAnchor="middle">
        {hero}
      </text>
      <text x={c} y={c + 22} className="chherol" textAnchor="middle">
        {heroLabel}
      </text>
    </svg>
  )
}

/* ==========================================================================
 * LINE CHART with a crosshair
 * Used by the horse race (cumulative) and the platform trend (one measure at
 * a time). ONE y-axis, always: the metric picker SWAPS the series rather than
 * adding a second scale.
 * ======================================================================== */

export interface LineSeries {
  name: string
  values: number[]
  color: string
  /** Draw as a pooled/neutral series: thinner, muted, and excluded from the
   *  direct end-labels so it never competes with a named runner. */
  muted?: boolean
}

export function LineChart({
  series,
  colors: _colors,
  xLabels,
  format = fmtInt,
  markers = [],
  height = 300,
  label = 'Trend',
}: {
  series: LineSeries[]
  colors?: string[]
  xLabels?: string[]
  format?: Fmt
  /** Computed annotations — a lead change, never a hand-placed marker. */
  markers?: Array<{ at: number; text: string }>
  height?: number
  label?: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  if (series.length === 0) return <p className="hint">NO TREND DATA IN THIS WINDOW.</p>
  const span = Math.max(...series.map((s) => s.values.length), 0)
  if (span === 0) return <p className="hint">NO TREND DATA IN THIS WINDOW.</p>

  const w = 1000
  const h = height
  const padL = 56
  const padR = 96
  const padT = 18
  const padB = 30
  const max = Math.max(...series.flatMap((s) => s.values), 1)
  const x = (i: number) => padL + (span <= 1 ? 0 : (i / (span - 1)) * (w - padL - padR))
  const y = (v: number) => padT + (1 - v / max) * (h - padT - padB)

  // Four gridlines, on clean round numbers. Recessive: a 1px hairline one
  // step off the surface, never dashed.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max)

  return (
    <div className="chwrap">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="plot wide"
        role="img"
        aria-label={label}
        onPointerMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const px = ((e.clientX - box.left) / box.width) * w
          const i = Math.round(((px - padL) / (w - padL - padR)) * (span - 1))
          setHover(Number.isFinite(i) ? Math.max(0, Math.min(span - 1, i)) : null)
        }}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={y(t)} x2={w - padR} y2={y(t)} stroke="var(--a-line)" strokeWidth={1} />
            <text x={padL - 8} y={y(t) + 4} className="chaxis" textAnchor="end">
              {format(t)}
            </text>
          </g>
        ))}

        {markers.map((m) => (
          <g key={`${m.at}-${m.text}`}>
            <line x1={x(m.at)} y1={padT} x2={x(m.at)} y2={h - padB} stroke="var(--a-line2)" strokeWidth={1} />
            <text x={x(m.at) + 6} y={padT + 12} className="chmark">
              {m.text}
            </text>
          </g>
        ))}

        {/* Muted series are drawn FIRST so a named runner is never hidden
            behind the pooled tail. */}
        {[...series]
          .sort((a, b) => Number(b.muted ?? false) - Number(a.muted ?? false))
          .map((s) => {
            const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
            return (
              <polyline
                key={s.name}
                points={pts}
                fill="none"
                stroke={s.color}
                strokeWidth={s.muted ? 1.5 : 2}
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity={s.muted ? 0.5 : 1}
              />
            )
          })}

        {/* End DOTS on every named series; a direct end-LABEL on only the one
            in front.
            WHY ONLY ONE. Direct labels work because they are sparing, and they
            work only while the series separate at the right edge. Five people
            on a young platform converge near the bottom of the axis, and the
            first render of this chart stacked four labels on top of each other
            at the same pixel — worse than no label at all. The legend is the
            dependable identity channel here; the crosshair tooltip carries
            every value at every x. */}
        {(() => {
          const named = series.filter((s) => !s.muted && s.values.length > 0)
          const leader = named.reduce<LineSeries | null>(
            (best, s) => (best === null || (s.values[s.values.length - 1] ?? 0) > (best.values[best.values.length - 1] ?? 0) ? s : best),
            null,
          )
          return named.map((s) => {
            const last = s.values.length - 1
            return (
              <g key={`end-${s.name}`}>
                <circle
                  cx={x(last)}
                  cy={y(s.values[last]!)}
                  r={4}
                  fill={s.color}
                  stroke="var(--a-panel)"
                  strokeWidth={GAP}
                />
                {s === leader ? (
                  <text x={x(last) + 10} y={y(s.values[last]!) + 4} className="chend">
                    {s.name}
                  </text>
                ) : null}
              </g>
            )
          })
        })()}

        {hover !== null ? (
          <line x1={x(hover)} y1={padT} x2={x(hover)} y2={h - padB} stroke="var(--a-accent)" strokeWidth={1} />
        ) : null}

        {xLabels && xLabels.length > 0 ? (
          <>
            <text x={padL} y={h - 8} className="chaxis">
              {xLabels[0]}
            </text>
            <text x={w - padR} y={h - 8} className="chaxis" textAnchor="end">
              {xLabels[xLabels.length - 1]}
            </text>
          </>
        ) : null}
      </svg>

      {/* ONE tooltip, EVERY series: the pointer never has to land on a line to
          get a value. Values lead, labels follow. */}
      {hover !== null ? (
        <div className="chtip" role="status">
          <b>{xLabels?.[hover] ?? `Point ${hover + 1}`}</b>
          {series.map((s) => (
            <div key={s.name}>
              <i style={{ background: s.color }} />
              <em>{format(s.values[hover] ?? 0)}</em>
              <span>{s.name}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * BUMP
 * Position by week, best at the top. The lines cross where the order changed
 * -- which is the only thing this form is good at, and the thing the old
 * three-pip strip was trying to say with three dots.
 * ======================================================================== */

export function BumpChart({
  series,
  colors,
  weekLabels,
  label = 'Weekly position',
}: {
  series: Array<{ name: string; points: Array<{ week: number; rank: number | null; score: number }> }>
  colors: string[]
  weekLabels?: string[]
  label?: string
}) {
  if (series.length === 0) return <p className="hint">NO WEEKLY DATA IN THIS WINDOW.</p>
  const weeks = Math.max(...series.map((s) => s.points.length), 1)
  const depth = Math.max(...series.flatMap((s) => s.points.map((p) => p.rank ?? 0)), 1)
  const w = 1000
  const h = 60 + depth * 46
  const padL = 60
  const padR = 190
  const x = (i: number) => padL + (weeks <= 1 ? 0 : (i / (weeks - 1)) * (w - padL - padR))
  const y = (rank: number) => 34 + (rank - 1) * 46

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="plot wide" role="img" aria-label={label} preserveAspectRatio="xMinYMin meet">
      {Array.from({ length: depth }, (_, i) => (
        <text key={i} x={padL - 12} y={y(i + 1) + 4} className="chaxis" textAnchor="end">
          {i + 1}
        </text>
      ))}
      {series.map((s, si) => {
        const color = slotColor(colors, si)
        // A week the group could not field the floor is a GAP, not a loss, so
        // the line breaks rather than dropping to last place.
        const runs: Array<Array<{ i: number; rank: number }>> = []
        let run: Array<{ i: number; rank: number }> = []
        s.points.forEach((p, i) => {
          if (p.rank === null) {
            if (run.length > 0) runs.push(run)
            run = []
          } else {
            run.push({ i, rank: p.rank })
          }
        })
        if (run.length > 0) runs.push(run)
        const lastPoint = [...s.points].reverse().find((p) => p.rank !== null)
        return (
          <g key={s.name}>
            {runs.map((r, ri) => (
              <polyline
                key={ri}
                points={r.map((p) => `${x(p.i)},${y(p.rank)}`).join(' ')}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {s.points.map((p, i) =>
              p.rank === null ? null : (
                <circle key={i} cx={x(i)} cy={y(p.rank)} r={5} fill={color} stroke="var(--a-panel)" strokeWidth={GAP}>
                  <title>{`${s.name} — week ${i + 1}, position ${p.rank}, ${fmtInt(p.score)} tokens per person`}</title>
                </circle>
              ),
            )}
            {lastPoint ? (
              <text
                x={x(s.points.lastIndexOf(lastPoint)) + 12}
                y={y(lastPoint.rank!) + 4}
                className="chend"
              >
                {s.name}
              </text>
            ) : null}
          </g>
        )
      })}
      {weekLabels?.map((l, i) => (
        <text key={l} x={x(i)} y={h - 8} className="chaxis" textAnchor="middle">
          {l}
        </text>
      ))}
    </svg>
  )
}

/* ==========================================================================
 * THE RACE TRACK
 * A field of runners with a timeline under it. Position along a lane encodes
 * the cumulative measure; the slider that drives `at` lives in the view.
 * ======================================================================== */

export interface TrackLane {
  name: string
  /** Cumulative values, one per day. Monotonic by construction. */
  values: number[]
  /** Printed beside the name. A lane without an `n` invites a headcount
   *  reading of a total that is not one. */
  active: number
  members: number
}

/**
 * WHY THIS SCALE, AND WHAT WAS REJECTED. Written here rather than in a commit
 * message because the next person to look at a bunched field will want to
 * "fix" it.
 *
 * The track is LINEAR in the measure, and the axis runs 0 to the LEADER'S
 * value at the scrubbed moment. Departments differ by roughly fifty times on
 * this platform, so most of the field sits in the first tenth of the track,
 * and that is the finding rather than a rendering fault.
 *
 * Three things make a bunched linear field readable, and together they are
 * why it was chosen over the alternatives:
 *
 *  1. ONE LANE PER RUNNER. Marks never overlap however close the positions
 *     get, so no label collides and every name stays legible. The classic
 *     objection to a bunched field is overplotting, and lanes remove it.
 *  2. EVERY LANE IS DIRECT-LABELLED WITH ITS OWN VALUE. The tail is readable
 *     as numbers even where it is not readable as distance, so nothing is
 *     lost — only the comparison BETWEEN two small runners is hard, and that
 *     is honestly hard.
 *  3. THE AXIS RESCALES WITH THE SCRUB, so the leader always reaches the
 *     finish and the field's spread is the shape you watch change.
 *
 * REJECTED — a log axis. It is honest on a bar chart and dishonest here: the
 * whole affordance of a race track is DISTANCE COVERED, and on a log track a
 * runner standing half way along has about the square root of the leader's
 * tokens. No reader infers that from a running track, and a label saying so
 * does not undo the picture.
 *
 * REJECTED — a rank-ordered track (x = position, evenly spaced). Honest, and
 * it makes a tidy chart, but it answers "who is ahead", which the lane order
 * and the position badge already answer, and it throws away the magnitude
 * that was the reason for asking.
 */
export function RaceTrack({
  lanes,
  at,
  colors,
  format = fmtInt,
  label = 'Race track',
}: {
  lanes: TrackLane[]
  /** Index into `values` — the scrubbed moment. */
  at: number
  colors: string[]
  format?: Fmt
  label?: string
}) {
  if (lanes.length === 0) return <p className="hint">NO RUNNERS IN THIS WINDOW.</p>

  const w = 1000
  const band = 34
  const h = lanes.length * band + 46
  const padL = 210
  const padR = 128
  const plot = w - padL - padR

  const valueAt = (l: TrackLane) => l.values[Math.min(at, l.values.length - 1)] ?? 0
  const max = Math.max(...lanes.map(valueAt), 1)
  // Position at the scrubbed moment, best first. The LANES do not reorder --
  // see `buildTrack` -- so this badge is how an overtake reads as a number
  // while the runner the eye is following stays on its own row.
  const standing = [...lanes].sort((a, b) => valueAt(b) - valueAt(a))
  const positionOf = new Map(standing.map((l, i) => [l.name, i + 1]))

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="plot wide" role="img" aria-label={label} preserveAspectRatio="xMinYMin meet">
      {/* The finish line. One rule, labelled with what reaching it means, so
          the scale is stated on the chart and not only in the caption. */}
      <line x1={padL + plot} y1={10} x2={padL + plot} y2={h - 26} stroke="var(--a-line2)" strokeWidth={1} />
      <text x={padL + plot} y={h - 10} className="chaxis" textAnchor="end">
        {format(max)}
      </text>
      <text x={padL} y={h - 10} className="chaxis">
        0
      </text>

      {lanes.map((l, i) => {
        const color = slotColor(colors, i)
        const v = valueAt(l)
        const x = padL + (v / max) * plot
        const y = 10 + i * band + band / 2
        // The value rides just ahead of the runner, and flips behind it at the
        // front of the track so it never runs off the plot.
        const near = x > padL + plot - 92
        return (
          <g key={l.name}>
            <title>{`${l.name} — ${format(v)} cumulative tokens, position ${positionOf.get(l.name)}, ${l.active} of ${l.members} active`}</title>
            <text x={0} y={y + 4} className="chcat">
              {`${positionOf.get(l.name)}. ${l.name}`}
            </text>
            <text x={padL - 12} y={y + 4} className="chnote" textAnchor="end">
              n={l.active}
            </text>
            {/* The lane itself: a hairline one step off the surface, solid. */}
            <line x1={padL} y1={y} x2={padL + plot} y2={y} stroke="var(--a-line)" strokeWidth={1} />
            {/* Distance covered, then the runner on its leading edge. */}
            <line x1={padL} y1={y} x2={x} y2={y} stroke={color} strokeWidth={3} strokeLinecap="round" />
            <circle cx={x} cy={y} r={7} fill={color} stroke="var(--a-panel)" strokeWidth={GAP} />
            <text
              x={near ? x - 14 : x + 14}
              y={y + 4}
              className="chval"
              textAnchor={near ? 'end' : 'start'}
            >
              {format(v)}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

/* ==========================================================================
 * HEATMAP
 * A dense grid of one measure. SEQUENTIAL, one hue, low values receding
 * toward the surface -- never a rainbow, and never a categorical palette
 * pressed into service as a value scale.
 * ======================================================================== */

/** Parses `#rgb`/`#rrggbb` into components. Anything else returns null and the
 *  caller falls back rather than drawing a colour it made up. */
function parseHex(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const raw = m[1]!
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/**
 * A sequential ramp: the chart SURFACE at zero, one hue at the maximum.
 *
 * ONE HUE, and the low end recedes into the surface, which is what a
 * sequential scale is for. Anchoring at the surface rather than at white
 * makes the same function correct in both directions: on the dark cabinet the
 * ramp runs dark to bright, on the daylight themes light to dark, and neither
 * needs its own table.
 */
export function sequentialColor(surface: string, hue: string, t: number): string {
  const a = parseHex(surface)
  const b = parseHex(hue)
  if (!a || !b) return hue
  const k = Math.max(0, Math.min(1, t))
  const mix = a.map((v, i) => Math.round(v + (b[i]! - v) * k))
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/** Relative luminance, for deciding whether a printed value sits on the cell
 *  in ink or in the surface colour. Cheap sRGB approximation: the decision is
 *  binary and the cells are large. */
function luminance(hex: string): number {
  const c = parseHex(hex)
  if (!c) return 0
  const [r, g, b] = c.map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export interface HeatCell {
  row: string
  col: string
  value: number
}

export function Heatmap({
  rows,
  cols,
  cells,
  surface,
  hue,
  format = fmtInt,
  unit,
  label = 'Heatmap',
  fillTile = false,
}: {
  /** Row order IS the seriation. Sorted by the caller; see the view. */
  rows: string[]
  cols: string[]
  cells: HeatCell[]
  /** The chart surface this is drawn on, so the ramp's zero recedes into it. */
  surface: string
  /** The single hue the ramp climbs to. */
  hue: string
  format?: Fmt
  /** Printed on the legend. A colour scale with no unit is a decoration. */
  unit: string
  label?: string
  /**
   * Off by default: the viewBox is a fixed 1000-unit-wide canvas and the
   * grid is drawn into whatever fraction of it the column count fills, which
   * is fine when there are enough columns to use most of it (the
   * leaderboard's people x server/tool grid, up to 14 columns).
   *
   * On: the viewBox is sized to the grid ITSELF -- no measured width, no
   * ResizeObserver, just a tighter viewBox plus width:100%/height:auto doing
   * the scaling -- and the cell size is chosen to hit a wide target aspect
   * so a SPARSE grid (Home's 6 hour-buckets used only 400 of the fixed
   * canvas's 1000 units, most of the tile drawn blank) fills its tile
   * instead. Kept opt-in rather than the new default because it also makes
   * an already-full grid print bigger, which changes the leaderboard's
   * printed size and wants its own visual check before it's turned on there.
   */
  fillTile?: boolean
}) {
  if (rows.length === 0 || cols.length === 0) return <p className="hint">NO ACTIVITY IN THIS WINDOW.</p>

  const lookup = new Map(cells.map((c) => [`${c.row}\u0000${c.col}`, c.value]))
  const max = Math.max(...cells.map((c) => c.value), 1)

  const gutter = 172
  const headH = 74
  let w: number
  let cell: number
  let rowH: number
  if (fillTile) {
    // Width:100%/height:auto makes the rendered box exactly the viewBox's
    // OWN aspect ratio -- there is no separate "fit into a box" step to hook
    // a measurement into. So the fix is picking a viewBox whose ratio
    // already reads as a wide tile, tuned to the ~900x375 tile this sits in.
    const TARGET_ASPECT = 2.4 // width:height
    rowH = 34
    const gridH = headH + rows.length * rowH + 10
    cell = Math.max(28, Math.min(170, (TARGET_ASPECT * gridH - gutter) / cols.length))
    w = gutter + cols.length * cell
  } else {
    w = 1000
    cell = Math.max(20, Math.min(38, Math.floor((w - gutter - 8) / cols.length)))
    rowH = Math.max(18, Math.min(30, cell))
  }
  const h = headH + rows.length * rowH + 10
  // A value in every cell is chaos at any real density. Print them only where
  // the cell is wide enough to hold one with padding; below that the tooltip
  // and the table view carry every number, which is the rule.
  const printValues = cell >= 34

  return (
    <div className="chwrap">
      <svg viewBox={`0 0 ${w} ${h}`} className="plot wide" role="img" aria-label={label} preserveAspectRatio="xMinYMin meet">
        {cols.map((c, ci) => (
          <text
            key={c}
            className="chaxis"
            transform={`translate(${gutter + ci * cell + cell / 2}, ${headH - 10}) rotate(-40)`}
          >
            {c}
          </text>
        ))}
        {rows.map((r, ri) => (
          <g key={r}>
            <text x={0} y={headH + ri * rowH + rowH / 2 + 4} className="chcat">
              {r}
            </text>
            {cols.map((c, ci) => {
              const v = lookup.get(`${r}\u0000${c}`) ?? 0
              const fill = v === 0 ? 'var(--a-line)' : sequentialColor(surface, hue, 0.18 + 0.82 * (v / max))
              const ink =
                v === 0
                  ? 'var(--a-ink-faint)'
                  : luminance(sequentialColor(surface, hue, 0.18 + 0.82 * (v / max))) > 0.42
                    ? '#101010'
                    : '#ffffff'
              return (
                <g key={c}>
                  <title>{`${r} · ${c} — ${format(v)} ${unit}`}</title>
                  {/* A 2px surface gap does the separating; no borders. */}
                  <rect
                    x={gutter + ci * cell}
                    y={headH + ri * rowH}
                    width={cell - GAP}
                    height={rowH - GAP}
                    rx={2}
                    fill={fill}
                  />
                  {printValues && v > 0 ? (
                    <text
                      x={gutter + ci * cell + (cell - GAP) / 2}
                      y={headH + ri * rowH + rowH / 2 + 4}
                      className="chcell"
                      textAnchor="middle"
                      fill={ink}
                    >
                      {format(v)}
                    </text>
                  ) : null}
                </g>
              )
            })}
          </g>
        ))}
      </svg>
      {/* THE LEGEND STATES THE UNIT. A continuous scale without one is a
          gradient, not a measurement. */}
      <div className="heatkey">
        <span>0</span>
        {[0.18, 0.36, 0.54, 0.72, 0.9, 1].map((t) => (
          <i key={t} style={{ background: sequentialColor(surface, hue, t) }} />
        ))}
        <span>
          {format(max)} {unit}
        </span>
      </div>
    </div>
  )
}

/* ==========================================================================
 * SMALL MULTIPLES
 * One tiny area per category on its OWN scale, so the SHAPE of a small
 * category is readable next to a large one. The counts live in the table
 * beneath: a small multiple answers "what is it doing", never "how big".
 * ======================================================================== */

export function SmallMultiple({
  values,
  color,
  label,
}: {
  values: number[]
  color: string
  label: string
}) {
  const w = 120
  const h = 40
  const max = Math.max(...values, 1)
  const step = values.length > 1 ? w / (values.length - 1) : w
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 3) - 1).toFixed(1)}`)
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="chsm" role="img" aria-label={label}>
      <polyline points={`0,${h} ${pts.join(' ')} ${w},${h}`} fill={color} opacity={0.12} stroke="none" />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
